import AsyncStorage from '@react-native-async-storage/async-storage';
import CryptoJS from 'crypto-js';
import * as SecureStore from 'expo-secure-store';
import 'react-native-get-random-values';
import { v4 as uuidv4 } from 'uuid';

/**
 * Encrypted write-through local drafts for dynamic forms (CD-222).
 *
 * The state machine and the in-progress section's field values live in memory
 * only; an app kill mid-section loses them. Drafts shrink that loss window to
 * the debounce interval. Payloads contain participant answers (PHI), so they
 * are AES-encrypted at rest in AsyncStorage with a per-device cipher key kept
 * in the Keychain/Keystore via SecureStore.
 */

export interface FormDraftPayload<FormValuesType = any> {
  formValues: Partial<FormValuesType>;
  statePath?: string;
  formTemplateVersion?: number;
  /** entityDataId or participantEventLogId; absent until the first save */
  instanceId?: number | string;
  savedAt: number;
}

export type FormDraftInput<FormValuesType = any> = Omit<
  FormDraftPayload<FormValuesType>,
  'savedAt'
>;

const DRAFT_KEY_PREFIX = 'formDraft:';
const CIPHER_KEY_STORE_KEY = 'formDraftCipherKey';

/** Drafts older than this are stale — discard rather than restore. */
export const FORM_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Screens gate their first render on the draft check, so a hung storage or
 * Keychain call must degrade to "no draft" rather than blocking the form.
 */
export const FORM_DRAFT_LOAD_TIMEOUT_MS = 3000;

const withTimeout = <T>(
  work: Promise<T>,
  fallback: T,
  ms: number,
  onTimeout?: () => void
): Promise<T> =>
  new Promise<T>(resolve => {
    const timer = setTimeout(() => {
      onTimeout?.();
      resolve(fallback);
    }, ms);
    work
      .then(result => {
        clearTimeout(timer);
        resolve(result);
      })
      .catch(() => {
        clearTimeout(timer);
        resolve(fallback);
      });
  });

/**
 * One key per user and template — the instance identity lives in the payload
 * and is validated on restore. Keeping the key stable across the first save
 * (which assigns the entity id mid-flow) avoids a key migration entirely.
 */
export const formDraftKey = ({
  userId,
  formTemplateId
}: {
  userId: number | string;
  formTemplateId: number | string;
}): string => `${DRAFT_KEY_PREFIX}${userId}:${formTemplateId}`;

/** Route params arrive as strings while created ids are numbers. */
const sameInstance = (a?: number | string, b?: number | string): boolean =>
  (a == null && b == null) || String(a) === String(b);

/**
 * The cipher key is generated once per device and lives in the
 * Keychain/Keystore. The lookup is shared so concurrent first accesses cannot
 * race two different keys into existence.
 */
let cipherKeyLookup: Promise<string> | undefined;

const getCipherKey = (): Promise<string> => {
  if (!cipherKeyLookup) {
    cipherKeyLookup = (async () => {
      const stored = await SecureStore.getItemAsync(CIPHER_KEY_STORE_KEY);
      if (stored) return stored;
      const generated = `${uuidv4()}${uuidv4()}`;
      await SecureStore.setItemAsync(CIPHER_KEY_STORE_KEY, generated);
      return generated;
    })().catch(error => {
      // do not cache a rejection — retry on the next access
      cipherKeyLookup = undefined;
      throw error;
    });
  }
  return cipherKeyLookup;
};

export const saveFormDraft = async (
  draftKey: string,
  draft: FormDraftInput,
  savedAt: number = Date.now()
): Promise<void> => {
  try {
    const payload: FormDraftPayload = { ...draft, savedAt };
    const cipherKey = await getCipherKey();
    const ciphertext = CryptoJS.AES.encrypt(
      JSON.stringify(payload),
      cipherKey
    ).toString();
    await AsyncStorage.setItem(draftKey, ciphertext);
  } catch {
    // a failed draft write must never break the form — the draft is a
    // best-effort recovery net, not required state
  }
};

export interface FormDraftLoadOptions {
  formTemplateVersion?: number;
  instanceId?: number | string;
  now?: number;
  /**
   * Whether the form may resume at a stored statePath. A path that fails is
   * dropped from the result and from storage; the values are kept.
   */
  canResume?: (statePath: string) => boolean;
}

export const loadFormDraft = (
  draftKey: string,
  options: FormDraftLoadOptions
): Promise<FormDraftPayload | undefined> => {
  // once the caller has timed out and moved on, the still-running load must
  // not delete the key on an invalid verdict — its snapshot is stale and a
  // fresh draft may have been written to the same key since
  const raceState = { timedOut: false };
  return withTimeout(
    loadFormDraftUnbounded(draftKey, options, raceState),
    undefined,
    FORM_DRAFT_LOAD_TIMEOUT_MS,
    () => {
      raceState.timedOut = true;
    }
  );
};

const loadFormDraftUnbounded = async (
  draftKey: string,
  {
    formTemplateVersion,
    instanceId,
    now = Date.now(),
    canResume
  }: FormDraftLoadOptions,
  raceState: { timedOut: boolean }
): Promise<FormDraftPayload | undefined> => {
  // a logout wipe during the load invalidates whatever it read
  const epoch = draftEpoch;
  let ciphertext: string | null = null;
  try {
    ciphertext = await AsyncStorage.getItem(draftKey);
  } catch {
    return undefined;
  }
  if (!ciphertext) return undefined;
  try {
    const cipherKey = await getCipherKey();
    const plaintext = CryptoJS.AES.decrypt(ciphertext, cipherKey).toString(
      CryptoJS.enc.Utf8
    );
    const payload = JSON.parse(plaintext) as FormDraftPayload;
    const expired = now - payload.savedAt > FORM_DRAFT_TTL_MS;
    const versionMismatch =
      formTemplateVersion !== undefined &&
      payload.formTemplateVersion !== undefined &&
      payload.formTemplateVersion !== formTemplateVersion;
    if (expired || versionMismatch) {
      if (!raceState.timedOut) await clearFormDraft(draftKey);
      return undefined;
    }
    // A draft for a different instance of this template must not leak into
    // the one being opened; keep it (don't delete) so its own instance can
    // usually still recover — overwrites, the completion clear, and TTL bound
    // its lifetime. Recovery is best-effort, not guaranteed: a kill in the
    // brief window right after entity creation can persist a write whose
    // instanceId hadn't caught up yet, which then mismatches here and only
    // costs the resume point (the server keeps every committed section).
    if (!sameInstance(payload.instanceId, instanceId)) {
      return undefined;
    }
    if (epoch !== draftEpoch) return undefined;
    if (payload.statePath && canResume && !canResume(payload.statePath)) {
      const cleaned: FormDraftInput = {
        formValues: payload.formValues,
        formTemplateVersion: payload.formTemplateVersion,
        instanceId: payload.instanceId
      };
      // keep the original timestamp: scrubbing a path must not extend the
      // TTL of answers at rest
      if (!raceState.timedOut) {
        await saveFormDraft(draftKey, cleaned, payload.savedAt);
        if (epoch !== draftEpoch) {
          await clearFormDraft(draftKey);
          return undefined;
        }
      }
      return { ...cleaned, savedAt: payload.savedAt };
    }
    return payload;
  } catch {
    // undecipherable / corrupt (e.g. cipher key rotated) — discard
    if (!raceState.timedOut) await clearFormDraft(draftKey);
    return undefined;
  }
};

export const clearFormDraft = async (draftKey: string): Promise<void> => {
  try {
    await AsyncStorage.removeItem(draftKey);
  } catch {
    // best-effort; TTL expires anything left behind
  }
};

/**
 * Bumped by clearAllFormDrafts so writes queued before a logout wipe are
 * invalidated: a form screen can unmount (and flush) after the wipe, and its
 * pending draft must not resurrect the previous account's answers.
 */
let draftEpoch = 0;

/** Remove every stored draft — called on logout. */
export const clearAllFormDrafts = async (): Promise<void> => {
  draftEpoch++;
  try {
    const keys = await AsyncStorage.getAllKeys();
    const draftKeys = keys.filter(key => key.startsWith(DRAFT_KEY_PREFIX));
    if (draftKeys.length > 0) {
      await AsyncStorage.multiRemove(draftKeys);
    }
  } catch {
    // best-effort; TTL expires anything left behind
  }
};

/**
 * Restore precedence: the server copy always wins — the draft only fills
 * fields the server copy lacks (null/undefined). A draft must never overwrite
 * newer server data.
 *
 * Assumption: null/undefined on the server means "never answered". If a form
 * ever gains an affordance to explicitly clear an answered field server-side,
 * that cleared value would be indistinguishable and could be backfilled here.
 */
export const mergeDraftValues = <FormValuesType>(
  serverValues: Partial<FormValuesType>,
  draftValues?: Partial<FormValuesType>
): Partial<FormValuesType> => {
  if (!draftValues) return serverValues;
  const merged: Partial<FormValuesType> = { ...draftValues, ...serverValues };
  for (const key of Object.keys(merged) as (keyof FormValuesType)[]) {
    if (
      (merged[key] === null || merged[key] === undefined) &&
      draftValues[key] !== undefined
    ) {
      merged[key] = draftValues[key];
    }
  }
  return merged;
};

export interface DebouncedDraftWriter {
  /** Merge a partial draft update; persisted after the debounce interval. */
  write: (draft: Partial<FormDraftInput>) => void;
  /** Persist any pending update immediately (e.g. on a section transition). */
  flush: () => Promise<void>;
  /** Drop the accumulated draft without persisting (e.g. after completion). */
  cancel: () => void;
}

export const createDebouncedDraftWriter = (
  draftKey: string,
  delayMs: number,
  options: { canResume?: (statePath: string) => boolean } = {}
): DebouncedDraftWriter => {
  // updates accumulate across persists so a values-only write after a section
  // transition still carries the last statePath (and vice versa)
  let accumulated: Partial<FormDraftInput> = {};
  let dirty = false;
  let pendingEpoch = draftEpoch;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const persist = async () => {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
    if (!dirty) return;
    dirty = false;
    // queued before a logout wipe — drop rather than resurrect the previous
    // account's answers
    if (pendingEpoch !== draftEpoch) {
      accumulated = {};
      return;
    }
    await saveFormDraft(draftKey, {
      formValues: {},
      ...accumulated
    });
    // the wipe bumps the epoch before it removes keys, so a write that was
    // already past the check above can land after the removal — undo it
    if (pendingEpoch !== draftEpoch) await clearFormDraft(draftKey);
  };

  return {
    write: draft => {
      if (
        draft.statePath &&
        options.canResume &&
        !options.canResume(draft.statePath)
      ) {
        const rest = { ...draft };
        delete rest.statePath;
        draft = rest;
        // a bare resume-point write carries only identity fields; persisting
        // those alone would overwrite the stored values and resume point
        if (draft.formValues === undefined) return;
      }
      accumulated = { ...accumulated, ...draft };
      dirty = true;
      pendingEpoch = draftEpoch;
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(persist, delayMs);
    },
    flush: persist,
    cancel: () => {
      if (timer !== undefined) {
        clearTimeout(timer);
        timer = undefined;
      }
      accumulated = {};
      dirty = false;
    }
  };
};
